import { useState } from 'react';
import { categories } from '@/mocks/categories';

const formSubmitAddr = 'https://readdy.ai/api/form/d90di7g4jh3v1cgksln0';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (_) {
      // silently handle
    }
    setSubmitting(false);
    setSubmitted(true);
    setEmail('');
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Column 1 - Brand */}
          <div className="lg:col-span-4">
            <a href="/" className="inline-block font-heading text-2xl md:text-3xl font-bold tracking-tight text-background-50 cursor-pointer">
              itaobuy<span className="text-accent-500">.run</span>
            </a>
            <p className="mt-4 text-sm text-foreground-300 leading-relaxed max-w-xs">
              The smart product sourcing spreadsheet system for modern resellers. Discover, compare, and scale with structured data.
            </p>
          </div>

          {/* Column 2 - Navigation */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-background-50 tracking-wide uppercase mb-4">Navigation</h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
              <li>
                <a href="#" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">
                  All Categories
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3 - Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-background-50 tracking-wide uppercase mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              <li><a href="#how-it-works" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">How It Works</a></li>
              <li><a href="#features" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">Features</a></li>
              <li><a href="#comparison" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">Comparison</a></li>
              <li><a href="#safety" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">Safety</a></li>
              <li><a href="#blog" className="text-sm text-foreground-400 hover:text-background-50 smooth-transition cursor-pointer">SEO Guide</a></li>
            </ul>
          </div>

          {/* Column 4 - Newsletter */}
          <div className="lg:col-span-4">
            <h4 className="text-sm font-semibold text-background-50 tracking-wide uppercase mb-4">Subscribe</h4>
            <p className="text-sm text-foreground-400 mb-4 leading-relaxed">
              Subscribe for sourcing tips and product updates.
            </p>
            {submitted ? (
              <p className="text-sm text-accent-400 font-medium">Thank you for subscribing!</p>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-2">
                <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-800 border border-foreground-700 rounded-full text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500 smooth-transition"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-10 h-10 flex items-center justify-center bg-accent-500 hover:bg-accent-600 rounded-full smooth-transition flex-shrink-0 cursor-pointer disabled:opacity-60"
                >
                  <i className={`text-white text-sm ${submitting ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'}`}></i>
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} itaobuy.run. All rights reserved.
          </p>
          <p className="text-xs text-foreground-500">
            Educational sourcing system. Not affiliated with any marketplace.
          </p>
        </div>
      </div>

      {/* Decorative background text */}
      <div className="overflow-hidden pb-6 px-4">
        <span className="block text-[6rem] md:text-[9rem] font-heading font-bold text-foreground-800/20 leading-none select-none whitespace-nowrap">
          ITAOBUY
        </span>
      </div>
    </footer>
  );
}